import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIsDesktopLayout } from '../../../hooks/useMediaQuery';
import { useMainNavigation } from './MainNavigation/hooks';
import { useProvidersNavigation } from './ProvidersNavigation/hooks';
import { useCategoriesNavigation } from './CategoriesNavigation/hooks';
import { sendAnalyticsData } from '../../../services/analytics';
import type {
  categoryLogoImageSelector,
  providerLogoImagePathSelector,
} from '../../../redux/reducers/app/configuration';
import type { SidebarNavigation } from './types';

export const useRedirectToDetailsPageCallbackWithAnalyticsData = (
  routeBuilder: (id?: string) => string
) => {
  const history = useHistory();

  return useCallback(
    ({ id, analyticsData }) => {
      if (analyticsData) {
        sendAnalyticsData(analyticsData);
      }

      history.push(routeBuilder(id));
    },
    [history, routeBuilder]
  );
};

export const useEntityIconPathCallback = (
  imagePathSelector:
    | typeof providerLogoImagePathSelector
    | typeof categoryLogoImageSelector,
  pathBuilder: (path: string, id: string, isDesktop?: boolean) => string
): ((id: string) => string) => {
  const path = useSelector(imagePathSelector) as string;
  const isDesktop = useIsDesktopLayout();

  return useCallback(
    (id: string) => pathBuilder(path, id, isDesktop),
    [path, isDesktop, pathBuilder]
  );
};

export const useSidebarNavigation = (): SidebarNavigation => {
  const mainNavigationItems = useMainNavigation();
  const { items: providers, isLoading: isLoadingProviders } =
    useProvidersNavigation();
  const { items: categories, isLoading: isLoadingCategories } =
    useCategoriesNavigation();

  return useMemo(
    () => ({
      main: mainNavigationItems,
      categories,
      providers,
      isLoading: isLoadingCategories || isLoadingProviders,
    }),
    [
      mainNavigationItems,
      isLoadingCategories,
      isLoadingProviders,
      providers,
      categories,
    ]
  );
};
